import { computed, inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { APP_CONFIG } from '@/app/core/config/app-config.token';
import { ToastService } from '@/app/shared/ui/notifications/toast.service';
import { StaffBajaValidationRepository } from '../services/staff-baja-validation.repository';
import { StaffBajaEliminarRepository } from '../services/staff-baja-eliminar.repository';
import {
  StaffBajaEliminarResult,
  StaffBajaValidationResult,
  StaffBajaWizardStep,
} from '../models/staff-baja.model';
import { StaffType } from '../models/staff.model';
import { perType, StaffTypeSelection } from './staff-type.selection';
import { notifyStaffBatchLimitExceeded } from './staff-batch-limit-exceeded-notice';
import { fileTooLargeMessage, resolveStaffValidationError } from './staff-upload-error.util';

const GENERIC_VALIDATION_ERROR =
  'No se pudo leer el archivo. Verifica que sea un archivo .xlsx válido, sin daños ni ' +
  'cambios de extensión, y vuelve a intentarlo.';

const GENERIC_DELETE_ERROR =
  'No se pudo procesar la baja de los registros exitosos. Intenta nuevamente.';

@Injectable()
export class StaffBajaStore {
  private readonly validationRepository = inject(StaffBajaValidationRepository);
  private readonly eliminarRepository = inject(StaffBajaEliminarRepository);
  private readonly config = inject(APP_CONFIG);
  private readonly toasts = inject(ToastService);
  private readonly selection = inject(StaffTypeSelection);

  readonly type = this.selection.type;

  private readonly _step = perType<StaffBajaWizardStep>(this.type, 1);
  private readonly _selectedFile = perType<File | null>(this.type, null);
  private readonly _validation = perType<StaffBajaValidationResult | null>(this.type, null);
  private readonly _isValidating = perType(this.type, false);
  private readonly _validationError = perType<string | null>(this.type, null);
  private readonly _eliminarResult = perType<StaffBajaEliminarResult | null>(this.type, null);
  private readonly _isProcessingSuccessful = perType(this.type, false);
  private readonly _eliminarError = perType<string | null>(this.type, null);

  readonly step = this._step.current;
  readonly validation = this._validation.current;
  readonly isValidating = this._isValidating.current;
  readonly validationError = this._validationError.current;
  readonly eliminarResult = this._eliminarResult.current;
  readonly isProcessingSuccessful = this._isProcessingSuccessful.current;
  readonly eliminarError = this._eliminarError.current;

  readonly selectedFileName = computed(() => this._selectedFile.current()?.name ?? null);
  readonly canProcess = computed(() => this._selectedFile.current() !== null);

  selectType(type: StaffType): void {
    if (this.type() === type) return;
    this.selection.select(type);
  }

  selectFile(file: File): void {
    if (file.size > this.config.staff.maxUploadBytes) {
      this._selectedFile.set(null);
      this._validationError.set(fileTooLargeMessage(this.config.staff.maxUploadBytes));
      return;
    }

    this._selectedFile.set(file);
    this._validationError.set(null);
  }

  async processFormatValidation(): Promise<void> {
    const file = this._selectedFile.current();
    if (!file || this.isValidating()) return;

    if (file.size > this.config.staff.maxUploadBytes) {
      this._validationError.set(fileTooLargeMessage(this.config.staff.maxUploadBytes));
      return;
    }

    const type = this.type();
    this._isValidating.set(true, type);
    this._validationError.set(null, type);
    try {
      const result = await firstValueFrom(this.validationRepository.validate(file, type));
      this._validation.set(result, type);
      this._step.set(2, type);
    } catch (cause) {
      if (notifyStaffBatchLimitExceeded(this.toasts, cause)) return;
      this._validationError.set(
        resolveStaffValidationError(cause, {
          maxUploadBytes: this.config.staff.maxUploadBytes,
          genericMessage: GENERIC_VALIDATION_ERROR,
        }),
        type,
      );
    } finally {
      this._isValidating.set(false, type);
    }
  }

  backToUpload(): void {
    this._step.set(1);
  }

  async processSuccessful(): Promise<void> {
    const file = this._selectedFile.current();
    const validation = this._validation.current();
    if (!file || !validation || validation.totalOk === 0 || this.isProcessingSuccessful()) return;

    const type = this.type();
    this._isProcessingSuccessful.set(true, type);
    this._eliminarError.set(null, type);
    try {
      const result = await firstValueFrom(this.eliminarRepository.eliminar(file, type));
      this._eliminarResult.set(result, type);
      this._step.set(3, type);
    } catch (cause) {
      this._eliminarError.set(this.resolveDeleteError(cause), type);
    } finally {
      this._isProcessingSuccessful.set(false, type);
    }
  }

  restart(): void {
    this._step.set(1);
    this._selectedFile.set(null);
    this._validation.set(null);
    this._validationError.set(null);
    this._eliminarResult.set(null);
    this._eliminarError.set(null);
  }

  private resolveDeleteError(cause: unknown): string | null {
    if (notifyStaffBatchLimitExceeded(this.toasts, cause)) return null;
    return GENERIC_DELETE_ERROR;
  }
}
